import { faEye } from "@fortawesome/free-solid-svg-icons"
import { Pagination, Tag, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import { GET_ITEM_LIMIT } from "constants/constants"
import { useEffect, useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import {
  AssetAnalysisRound,
  useListAssetStatusesQuery,
  useListAssetSubcategoriesQuery,
  useListRoundsQuery,
} from "services/aimsBackend"

type Option = { value: string; label: string }

const analysisStatusOptions: Option[] = [
  { value: "complete", label: "Complete" },
  { value: "incomplete", label: "Not complete" },
]

function roundStatusName(status: AssetAnalysisRound["status"]): string {
  return status?.name ?? ""
}

function analysisStatusLabel(status: AssetAnalysisRound["AnalysisStatus"]): string {
  return status === "complete" ? "Complete" : "Not complete"
}

export default function LIAListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<Option | null>(null)
  const [analysisStatusFilter, setAnalysisStatusFilter] = useState<Option | null>(null)
  const [subcategoryFilter, setSubcategoryFilter] = useState<Option | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)

  const { data: roundsList } = useListRoundsQuery({
    page: pageNumber,
    page_size: pageSize,
    contract_type: "LIA",
    status: statusFilter ? Number(statusFilter.value) : undefined,
    analysis_status: analysisStatusFilter?.value as "complete" | "incomplete" | undefined,
    subcategory: subcategoryFilter ? Number(subcategoryFilter.value) : undefined,
    search: search.trim() || undefined,
  })
  const { data: statuses = [] } = useListAssetStatusesQuery({ contract_type: "LIA" })
  const { data: subcategories = [] } = useListAssetSubcategoriesQuery()

  const rows = roundsList?.results ?? []

  const statusOptions = useMemo<Option[]>(
    () => statuses.map(status => ({ value: String(status.id), label: status.name })),
    [statuses],
  )
  const subcategoryOptions = useMemo<Option[]>(
    () =>
      subcategories.map(subcategory => ({
        value: String(subcategory.id),
        label: subcategory.title,
      })),
    [subcategories],
  )

  useEffect(() => {
    setPageNumber(1)
  }, [analysisStatusFilter, search, statusFilter, subcategoryFilter, pageSize])

  const columns: Array<ColumnShape<AssetAnalysisRound>> = [
    {
      key: "AssetId",
      title: "Asset ID",
      width: 110,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.AssetId || "-",
    },
    { key: "asset_admin1", dataKey: "asset_admin1", title: "Admin 1", width: 150, flexGrow: 1 },
    { key: "analysis_year", dataKey: "analysis_year", title: "Year", width: 90, flexGrow: 0 },
    { key: "aims_id", dataKey: "aims_id", title: "AIMS ID", width: 100, flexGrow: 0 },
    { key: "label", dataKey: "label", title: "Round", width: 110, flexGrow: 1 },
    {
      key: "status",
      title: "Status",
      width: 130,
      flexGrow: 1,
      cellRenderer: ({ rowData }) =>
        roundStatusName(rowData.status) ? (
          <Tag type="info" style={{ marginBottom: 0 }}>
            {roundStatusName(rowData.status)}
          </Tag>
        ) : (
          <span>-</span>
        ),
    },
    {
      key: "AnalysisStatus",
      title: "Analysis Status",
      width: 160,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <Tag type={rowData.AnalysisStatus === "complete" ? "success" : "warning"} style={{ marginBottom: 0 }}>
          {analysisStatusLabel(rowData.AnalysisStatus)}
        </Tag>
      ),
    },
    { key: "asset_category", dataKey: "asset_category", title: "Category", width: 130, flexGrow: 1 },
    { key: "asset_subcategory", dataKey: "asset_subcategory", title: "Subcategory", width: 150, flexGrow: 1 },
    { key: "image_date", dataKey: "image_date", title: "Image Date", width: 120, flexGrow: 0 },
    { key: "band_count", dataKey: "band_count", title: "Bands", width: 90, flexGrow: 0 },
    {
      key: "image_clearance",
      title: "Image Clearance",
      width: 150,
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <input type="checkbox" aria-label="Image clearance" checked={rowData.image_clearance} readOnly disabled />
      ),
    },
    {
      key: "actions",
      title: "",
      width: 140,
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <Button
          action={() => navigate(`/aims/AssetsView/preview/${rowData.asset}`)}
          label="View"
          icon={faEye}
          className="btn-full blue action-btn"
        />
      ),
    },
  ]

  return (
    <>
      <h2 className="m-0 mb-3 mt-2">LIA Rounds</h2>

      <div className="mb-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Search
          </p>
          <TextInput
            name="liaSearch"
            placeholder="Asset ID or AIMS ID"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Status
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={statusOptions}
            value={statusFilter}
            onChange={val => setStatusFilter(val)}
            placeholder="Select status"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Analysis Status
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={analysisStatusOptions}
            value={analysisStatusFilter}
            onChange={val => setAnalysisStatusFilter(val)}
            placeholder="Select analysis status"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Subcategory
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={subcategoryOptions}
            value={subcategoryFilter}
            onChange={val => setSubcategoryFilter(val)}
            placeholder="Select subcategory"
            isClearable
          />
        </div>
      </div>

      <div style={{ height: "100%", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={rows}
              width={width}
              height={height}
              rowKey="id"
              className="custom-table"
              rowHeight={40}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No LIA rounds to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Pagination
        totalItems={roundsList?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[10, 20, 50]}
        page={pageNumber}
        onChange={(event: any): void => {
          const nextPageSize = (event as any).pageSize || pageSize
          setPageSize(nextPageSize)
          setPageNumber(nextPageSize === pageSize ? (event as any).page || 1 : 1)
        }}
      />
    </>
  )
}
